// Numeric `role` carried on the JWT / user record.
export const ROLES = {
  SUPER_ADMIN: 1,
  EMPLOYEE: 2,
  CLIENT: 3,
  ADMIN: 4,
  MANAGER: 5,
  FINANCE: 6,
  // Org Admin: owns one organization. Gets the same full app as a super admin,
  // but the backend narrows its Employee / Attendance / Daily Working Report
  // data to its own org members.
  ORG_ADMIN: 7,
};

// Roles that get the full admin experience in the UI: complete menu, every
// permission granted client-side, admin-only buttons visible.
export const ADMIN_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.ORG_ADMIN];

export const isAdminRole = (role) => ADMIN_ROLES.includes(role);

// An Org Admin is an admin of its own org only, so it does NOT get the blanket
// "everything true" set. This mirrors the backend allowlist in
// configuration/orgAdminAccess.js — granting more here would only surface
// buttons whose API calls come back 403.
//
// appDownload is view-only on purpose: the installers are Lamynaals-wide
// artefacts, so an Org Admin downloads them but cannot upload or delete.
const ALL = { view: true, add: true, edit: true, delete: true };
const VIEW_ONLY = { view: true, add: false, edit: false, delete: false };

export const ORG_ADMIN_PERMISSIONS = {
  employee: ALL,
  timeTracker: VIEW_ONLY, // Work Activity + App & Site Usage
  dailyWorkingReport: VIEW_ONLY,
  projectDailyReport: VIEW_ONLY,
  timeLoggerAttendance: VIEW_ONLY,
  appDownload: VIEW_ONLY,
};

// Employees an Org Admin created. In the web they only ever need to grab the
// Time-Logger desktop installer — everything else belongs to Lamynaals.
export const ORG_EMPLOYEE_PERMISSIONS = {
  appDownload: VIEW_ONLY,
  timeTracker: VIEW_ONLY, // Work Activity + App & Site Usage — own data only
  dailyWorkingReport: VIEW_ONLY, // own data only
};

// Someone who belongs to an org but is not its admin.
export const isOrgEmployee = (role, orgId) =>
  !!orgId && role !== ROLES.ORG_ADMIN;

// Where a freshly logged-in account should land. Org accounts have no
// dashboard permission, so sending them to /home/dashboard (the old default)
// dropped them on the "no access" screen — each lands on its first real page.
export const landingPathFor = (role, orgId) => {
  if (role === ROLES.FINANCE) return "/welcome";
  if (role === ROLES.ORG_ADMIN) return "/home/employee";
  if (isOrgEmployee(role, orgId)) return "/home/time-tracker";
  return "/home/dashboard";
};

export const ROLE_LABELS = {
  [ROLES.SUPER_ADMIN]: "Admin",
  [ROLES.EMPLOYEE]: "Employee",
  [ROLES.CLIENT]: "Client",
  [ROLES.ADMIN]: "Admin",
  [ROLES.MANAGER]: "Manager",
  [ROLES.FINANCE]: "Finance",
  [ROLES.ORG_ADMIN]: "Org Admin",
};
